import { DocumentTextIcon, FolderIcon, TrashIcon, WrenchIcon } from '@heroicons/react/24/outline'
import { GetContextMenuItemsParams } from 'ag-grid-community'
import { AgGridReactProps } from 'ag-grid-react'
import classNames from 'classnames'
import { toast } from 'react-toastify'

import { FileManagerFile } from '../FileManager/fileManagerSlice'
import { openMaxViewer } from '../FileManager/utilities'
import { useDownloadDocumentsMutation } from '@/services/documents'
import { QuarterlyCustomerStatement, UserDocument } from '@/types/unlock'
import { toastDefaultOptions } from '@/utils/toast'
import {
  isSalesUserAndAppNotInPendingICSDelivery,
  isUserAllowedToSendToDocuSign,
} from '@/utils/user'

export function openFilesInMaxViewer(
  applicationExternalId: string,
  rawFiles: Partial<UserDocument>[]
) {
  const files = [
    ...rawFiles.map((file: Partial<FileManagerFile & QuarterlyCustomerStatement>) => ({
      id: file.id,
      externalId: file.uuid,
      fileName: file.original_filename,
      filePath: file.download_url,
      downloadPath: file?.download_url ?? '',
      tag: file.tag,
      uuid: file.uuid,
      readonly: false,
      createdAt: file.created_at,
      modifiedAt: file.modified_at,
      external_id: file.external_id,
      application_profile: file.application_profile,
    })),
  ]
  openMaxViewer(applicationExternalId, files)
}

export const useGetContentMenuItems = (isWorkviewsAdmin: boolean = false) => {
  const [downloadDocuments] = useDownloadDocumentsMutation()

  return (params: GetContextMenuItemsParams) => {
    const { context, node } = params
    if (!node) return null
    const selectedRows = params.api.getSelectedRows()
    const isICSDocGenerated = params.api?.getDataAsCsv().includes('Closing Statement')
    const _isSalesUserAndAppNotInPendingICSDelivery = isSalesUserAndAppNotInPendingICSDelivery({
      userGroups: context.loggedUser.groups,
      applicationStateIndex: context.application.applicationStateIndex,
    })
    const multipleRowsSelected = selectedRows.length > 1
    // AG-Grid does not select the row when the user right-clicks on it.
    // So we need to use the node instead to perform single-row actions.
    const targetDocuments = !multipleRowsSelected ? [node.data] : selectedRows

    if (node.group)
      return [
        {
          name: 'Expand/Collapse',
          action: () => {
            node.setExpanded(!node.expanded)
          },
        },
      ]

    const isSendToDocuSignEnabled = isUserAllowedToSendToDocuSign(context.loggedUser)

    const pricingVersion = context?.application?.pricingVersion

    const tooltipMessage = !isSendToDocuSignEnabled
      ? 'You do not have permission to use this feature. Please request eSignatures using Customer Messages.'
      : pricingVersion < 22
        ? `This feature is not supported for v${pricingVersion}. Please request eSignatures using Customer Messages.`
        : ''

    const defaultItems = [
      {
        name: 'Open in Max Viewer',
        disabled: !node,
        action: () => openFilesInMaxViewer(context.application.externalId, targetDocuments),
      },
      {
        name: 'Open in Browser Tab',
        action() {
          if (multipleRowsSelected) {
            for (const row of selectedRows) {
              window.open(row.download_url, '_blank')
            }
          } else {
            const file = node.data
            if (file?.download_url) {
              window.open(file.download_url, '_blank')
            }
          }
        },
      },
      {
        name: 'Download',
        disabled: _isSalesUserAndAppNotInPendingICSDelivery && isICSDocGenerated,
        async action() {
          const downloadToast = toast.loading('Downloading file(s)...', {
            ...toastDefaultOptions,
            theme: 'colored',
            type: 'info',
          })
          try {
            let [tag, documents] = ['', []]
            if (multipleRowsSelected) {
              tag = selectedRows[0].tag
              documents = selectedRows.map((row) => ({ ...row }))
            } else {
              tag = node.data.tag
              documents = [node.data]
            }
            const appName = context?.application?.externalId ?? 'Unknown'
            await downloadDocuments({
              documents,
              customDownloadFileName: `${tag} - ${appName}.zip`,
            }).unwrap()

            toast.update(downloadToast, {
              ...toastDefaultOptions,
              render: 'File(s) downloaded successfully',
              type: 'success',
              closeButton: true,
              autoClose: 5000,
              isLoading: false,
            })
          } catch (error) {
            if (error?.data?.includes('showSaveFilePicker')) {
              toast.dismiss(downloadToast)
            } else {
              toast.update(downloadToast, {
                ...toastDefaultOptions,
                render: 'Failed to download file(s)',
                type: 'error',
                isLoading: false,
              })
            }
          }
        },
        cssClasses: ['text-blue-800'],
      },
      {
        name: 'Tag as...',
        subMenu: [
          {
            name: 'Deal Files',
            subMenu: dealFilesSubTags.map((tag) => ({
              name: tag,
              action: () => {
                context.tagDocumentsAs(targetDocuments, `Deal Files/${tag}`)
              },
            })),
          },
          {
            name: 'Servicing',
            subMenu: servicingSubTags.map((tag) => ({
              name: tag,
              action: () => {
                context.tagDocumentsAs(targetDocuments, `Servicing/${tag}`)
              },
            })),
          },
          {
            name: 'Post Origination',
            subMenu: postOriginationSubTags.map((tag) => ({
              name: tag,
              action: () => {
                context.tagDocumentsAs(targetDocuments, `Post Origination/${tag}`)
              },
            })),
          },
        ],
        disabled: !node,
      },
      'separator',
      {
        name: 'Request eSignature',
        disabled:
          !node ||
          selectedRows.length > 1 ||
          (_isSalesUserAndAppNotInPendingICSDelivery && isICSDocGenerated),
        subMenu: [
          {
            name: 'Send to CX',
            action: () => {
              params.context?.setIsSendToCXOpen?.(true)
            },
            cssClasses: ['text-blue-800'],
          },
          {
            name: 'Send to DocuSign',
            disabled: !isSendToDocuSignEnabled,
            tooltip: tooltipMessage,
            action: () => {
              params.context?.setIsSendToDocuSignOpen?.(true)
            },
            cssClasses: ['text-blue-800'],
          },
        ],
      },
      'separator',
      {
        name: 'Rename',
        disabled: !node || selectedRows.length > 1,
        action: () => {
          params.context?.setIsRenameDocOpen?.(true)
        },
      },
      {
        name: 'Delete',
        disabled: !node,
        action: () => {
          params.context?.setIsConfirmDeleteOpen(true)
        },
        cssClasses: ['text-red-800'],
      },
      {
        name: 'Restore',
        disabled:
          selectedRows.length === 0 || selectedRows.length > 1 || selectedRows[0].tag !== 'Deleted',
        action: () => {
          params.context?.restoreDocument?.(selectedRows[0])
        },
        cssClasses: ['text-green-700'],
      },
    ]

    if (isWorkviewsAdmin) {
      return [...defaultItems, 'separator', 'copy', 'copyWithHeaders', 'separator', 'export']
    }
    return defaultItems
  }
}

export function saveGridState(params: AgGridReactProps, localStorageKey: string) {
  if (typeof window !== 'undefined') {
    const colState = params.columnApi.getColumnState()
    const filterModel = params.api.getFilterModel()
    if (colState || filterModel) {
      window.localStorage.setItem(
        localStorageKey,
        JSON.stringify({
          colState,
          filterModel,
        })
      )
    }
  }
}

export function restoreGridState(params: AgGridReactProps, localStorageKey: string) {
  if (typeof window !== 'undefined') {
    const gridState = JSON.parse(window.localStorage.getItem(localStorageKey))
    if (gridState) {
      params?.columnApi.applyColumnState({
        state: gridState?.colState,
        applyOrder: true,
      })
      params?.api.setFilterModel(gridState?.filterModel)
    }
  }
}

export const docTags = [
  'Deal Files',
  'Deal Files/AVM Docs',
  'Deal Files/Appraisal Docs',
  'Deal Files/CX Docs',
  'Deal Files/Co-Owner Docs',
  'Deal Files/Client Signed Docs',
  'Deal Files/Contract Docs',
  'Deal Files/Deferment-Forbearance Docs',
  'Deal Files/DTI Docs',
  'Deal Files/Digital Certificates',
  'Deal Files/Expired / Not Needed',
  'Deal Files/HEO Docs',
  'Deal Files/HOA Docs',
  'Deal Files/Inspection Docs',
  'Deal Files/NOO Docs',
  'Deal Files/Payoff Docs',
  'Deal Files/POA Docs',
  'Deal Files/Post-Closing Docs',
  'Deal Files/Rental Docs',
  'Deal Files/Solar Docs',
  'Deal Files/Title Docs',
  'Deal Files/Trust Docs',
  'Deal Files/z-Raw Data',
  'Servicing/Statements',
  'Servicing/Buyout Docs',
  'Servicing/Home Sale Docs',
  'Servicing/Subordination Docs',
  'Servicing/Unlock Refi Docs',
  'Servicing/Home Improvement Docs',
  'Servicing/Generated Docs',
  'Post Origination/Credit',
  'Post Origination/Lien Alerts',
  'Post Origination/Insurance',
  'Post Origination/Final Disposition',
  'Post Origination/Servicing',
  'Deleted',
]

const dealFilesSubTags = docTags
  .filter((tag) => tag.includes('Deal Files/'))
  .map((tag) => tag.split('/').at(1))

const servicingSubTags = docTags
  .filter((tag) => tag.includes('Servicing/'))
  .map((tag) => tag.split('/').at(1))

const postOriginationSubTags = docTags
  .filter((tag) => tag.includes('Post Origination/'))
  .map((tag) => tag.split('/').at(1))

const GroupName = ({ label }) => {
  const parts = label.split('/')

  const rootLabelClassnames = classNames(
    {
      'text-blue-700': parts[0].startsWith('Deal Files'),
      'text-green-700': parts[0].startsWith('Servicing'),
      'text-yellow-700': parts[0].startsWith('Post Origination'),
    },
    'self-center'
  )

  const rootLabelIconLookup = {
    'Deal Files': <FolderIcon className='w-5 h-5 stroke-1' />,
    Servicing: <WrenchIcon className='w-5 h-5 stroke-1' />,
    'Post Origination': <DocumentTextIcon className='w-5 h-5 stroke-1' />,
    Deleted: <TrashIcon className='w-5 h-5 stroke-1' />,
  }

  if (parts.length > 1) {
    return (
      <>
        <span className={rootLabelClassnames}>{rootLabelIconLookup?.[parts[0]]}</span>
        <span className={rootLabelClassnames}>{parts[0]}</span>
        <span className='mx-0.5'> - </span>
        <span>{parts[1]}</span>
      </>
    )
  }

  return (
    <>
      <span className={rootLabelClassnames}>{rootLabelIconLookup?.[parts[0]]}</span>
      <span className='text-blue-700'>{label.split('/').join(' - ')}</span>
    </>
  )
}

export const GroupNodeRenderer = ({ params, hasFilesCreatedinLast24h }) => {
  return (
    <>
      <div className='flex items-baseline gap-1'>
        <GroupName label={params.node.key} />
        <span>({params.node.allChildrenCount})</span>
        {hasFilesCreatedinLast24h && <span className='badge badge-primary badge-sm'>new</span>}
      </div>
    </>
  )
}
